import { createRouter, createWebHistory } from "vue-router";
import { shellScrollBehavior } from "./scroll-behavior";
import { installRouteFocus } from "./route-focus";
import { authGuard, useAuth0 } from "@auth0/auth0-vue";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { useOnboardingStore } from "@/stores/onboarding-store";
import { isInAppMessagingEnabled } from "@/services/inapp.service";
import { usePostHog } from "@/composables/usePostHog";
import { useIsMobile } from "@/composables/useIsMobile";
import HomeView from "@/views/HomeView.vue";

// Initialize PostHog
const { posthog } = usePostHog();
import CallbackView from "@/views/CallbackView.vue";
import NotFoundView from "@/views/NotFoundView.vue";
import SignupView from "@/views/SignupView.vue";
import SettingsProfileView from "@/views/SettingsProfileView.vue";
import SettingsStripeRedirect from "@/views/SettingsStripeRedirect.vue";
import SettingsPlansView from "@/views/SettingsPlansView.vue";
import SettingsBillingView from "@/views/SettingsBillingView.vue";
import SettingsConnectedAppsView from "@/views/SettingsConnectedAppsView.vue";
import PaymentSuccessView from "@/views/PaymentSuccessView.vue";
import PaymentCancelView from "@/views/PaymentCancelView.vue";
import SettingsWorkspacesView from "../views/SettingsWorkspaces.vue";
import OnboardingFlow from "@/views/OnboardingFlow.vue";

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: shellScrollBehavior,
  routes: [
    {
      path: "/",
      name: "home",
      beforeEnter: [
        authGuard,
        async (to, from, next) => {
          // Check if onboarding is required
          const onboardingStore = useOnboardingStore();
          const onboardingResult =
            await onboardingStore.checkOnboardingStatus();

          // If the onboarding check failed due to an auth error, the token
          // handler will redirect to login. Don't proceed further.
          if (onboardingResult?.error) {
            console.warn(
              "[Router] Onboarding status check failed, waiting for auth redirect",
            );
            return;
          }

          if (onboardingStore.isOnboardingRequired) {
            next({ name: "onboarding" });
            return;
          }

          // Continue with normal flow
          const workspaceStore = useWorkSpaceStore();
          await workspaceStore.fetchWorkspaces();
          const defaultWorkspaceUUID = workspaceStore.activeWorkspaceUUID;

          // Only redirect to onboarding if workspaces were successfully loaded
          // but none exist. If the fetch failed (auth error), the token handler
          // will redirect to login — don't send the user to onboarding.
          if (!defaultWorkspaceUUID) {
            if (workspaceStore.workspacesLoaded) {
              console.error("No active workspace found after login");
              next({ name: "onboarding" });
            } else {
              console.warn(
                "[Router] Workspace fetch failed, waiting for auth redirect",
              );
            }
            return;
          }

          next({
            name: "workspace-home",
            params: { workspaceUUID: defaultWorkspaceUUID },
          });
        },
      ],
    },
    {
      path: "/onboarding",
      name: "onboarding",
      component: OnboardingFlow,
      beforeEnter: authGuard,
    },
    {
      path: "/signup",
      name: "signup",
      component: SignupView,
    },
    {
      // Mobile-shell Account tab. On desktop viewports the view redirects
      // itself to /settings.
      path: "/account",
      name: "account",
      component: () => import("../views/MobileAccountView.vue"),
      beforeEnter: authGuard,
      // Mobile-shell chrome is driven by route meta: mobileTitle (top bar),
      // shellTab (active bottom tab), appPromo (install banner + sheet).
      meta: { mobileTitle: "Account", shellTab: "account" },
    },
    {
      path: "/settings",
      name: "settings",
      component: SettingsProfileView,
      beforeEnter: authGuard,
      meta: { mobileTitle: "Profile" },
    },
    {
      path: "/settings/plans",
      name: "settings-plans",
      component: SettingsPlansView,
      beforeEnter: authGuard,
      meta: { mobileTitle: "Plans" },
    },
    {
      path: "/settings/stripe-redirect/:promoCode",
      name: "settings-stripe-redirect",
      component: SettingsStripeRedirect,
      beforeEnter: authGuard,
    },
    {
      path: "/settings/billing",
      name: "settings-billing",
      component: SettingsBillingView,
      beforeEnter: authGuard,
      meta: { mobileTitle: "Billing" },
    },
    {
      path: "/settings/connected-apps",
      name: "settings-connected-apps",
      component: SettingsConnectedAppsView,
      beforeEnter: authGuard,
      meta: { mobileTitle: "Connected apps" },
    },
    {
      path: "/settings/workspaces",
      name: "settings-workspaces",
      component: SettingsWorkspacesView,
      beforeEnter: authGuard,
      meta: { mobileTitle: "Workspaces" },
    },
    {
      path: "/:workspaceUUID",
      name: "workspace-home",
      component: HomeView,
      beforeEnter: authGuard,
      // Mobile navigations are redirected to conversations by the guard;
      // the meta covers a desktop→mobile resize while parked here.
      meta: { mobileTitle: "Library", shellTab: "library", appPromo: true },
    },
    {
      path: "/:workspaceUUID/conversations",
      name: "conversations",
      component: () => import("../views/ConversationsView.vue"),
      beforeEnter: authGuard,
      meta: { mobileTitle: "Library", shellTab: "library", appPromo: true },
    },
    {
      path: "/:workspaceUUID/favorites",
      name: "favorites",
      component: () => import("../views/FavoritesView.vue"),
      beforeEnter: authGuard,
      meta: { mobileTitle: "Library", shellTab: "library", appPromo: true },
    },
    {
      path: "/:workspaceUUID/transcription/:audioFileUUID",
      name: "transcription",
      component: () => import("../views/TranscriptionView.vue"),
      beforeEnter: authGuard,
      // Library remains the active bottom tab on the detail screen.
      meta: { shellTab: "library" },
    },
    {
      path: "/share/:shareLinkUUID",
      name: "public-share",
      component: () => import("../views/PublicShareView.vue"),
      meta: { public: true }, // No authGuard - public access
    },
    {
      path: "/record/new",
      name: "record-new",
      beforeEnter: authGuard,
      redirect: () => {
        // This route just triggers the recording popup
        // The actual recording view requires an audioFileId
        return { name: "conversations" };
      },
    },
    {
      path: "/record/:audioFileId",
      name: "record",
      component: () => import("../views/RealTimeRecordingView.vue"),
      beforeEnter: authGuard,
    },
    {
      path: "/callback",
      name: "callback",
      component: CallbackView,
    },
    {
      // Post-signup share-conversion landing (reached via Auth0
      // appState.target). In the guard skip-list below, like /callback —
      // it must mount before any onboarding/workspace logic runs.
      path: "/share-signup-complete",
      name: "share-signup-complete",
      component: () => import("../views/ShareSignupCompleteView.vue"),
    },
    {
      path: "/payment/success",
      name: "payment-success",
      component: PaymentSuccessView,
    },
    {
      path: "/payment/cancel",
      name: "payment-cancel",
      component: PaymentCancelView,
    },
    {
      path: "/:catchAll(.*)",
      name: "Not Found",
      component: NotFoundView,
    },
  ],
});

// Accessibility: after each page change, focus the new page's heading so
// screen readers announce it (see route-focus.js).
installRouteFocus(router);

// Add a global beforeEach navigation guard
router.beforeEach(async (to, from, next) => {
  console.log(`[Router] beforeEach: ${from.name || "initial"} -> ${to.name}`);

  // Skip all logic for public routes (they don't need auth or workspace data)
  if (to.meta?.public) {
    next();
    return;
  }

  // Skip workspace logic for recording routes (handled by their own guards)
  if (to.name === "record" || to.name === "record-new") {
    next();
    return;
  }

  // Skip workspace logic for callback route - SDK handles this automatically
  if (to.name === "callback" || to.name === "share-signup-complete") {
    next();
    return;
  }

  // The mobile shell has no Home tab: Library is the landing screen. This
  // runs BEFORE the auth-loading early return below — it needs no data, and
  // a direct mobile deep-link to workspace-home must never transiently mount
  // HomeView (whose children fire analytics and promo surfaces).
  if (to.name === "workspace-home" && useIsMobile().isMobile.value) {
    next({
      name: "conversations",
      params: to.params,
      query: to.query,
      hash: to.hash,
    });
    return;
  }

  // Get auth state - IMPORTANT: Check isLoading first to avoid making API calls
  // while the SDK is still processing the callback
  const { isLoading, isAuthenticated } = useAuth0();

  // If auth is still loading, just proceed - don't fetch data yet.
  // The App.vue watcher will handle initialization once auth is complete.
  if (isLoading.value) {
    console.log("[Router] Auth still loading, skipping data fetch");
    next();
    return;
  }

  // If not authenticated, let the route's authGuard handle the redirect
  if (!isAuthenticated.value) {
    console.log("[Router] Not authenticated, proceeding to route guard");
    next();
    return;
  }

  // User is authenticated - now we can safely fetch workspace data
  const workspaceStore = useWorkSpaceStore();

  // Ensure workspaces are loaded before navigation
  if (!workspaceStore.workspaces.length) {
    await workspaceStore.fetchWorkspaces();
  }

  // Check if the route expects a workspaceUUID
  const routeRequiresWorkspace = to.matched.some((record) =>
    record.path.includes(":workspaceUUID"),
  );

  if (routeRequiresWorkspace) {
    const activeWorkspaceUUID = workspaceStore.activeWorkspaceUUID;

    // If no active workspace exists, redirect to home to let it handle workspace setup
    if (!activeWorkspaceUUID) {
      // Only redirect if we're not already on home
      if (to.name !== "home") {
        next({ name: "home" });
        return;
      }
      // If on home, let it proceed (it will handle missing workspace)
      next();
      return;
    }

    // Redirect if workspaceUUID is missing or invalid
    if (
      !to.params.workspaceUUID ||
      to.params.workspaceUUID !== activeWorkspaceUUID
    ) {
      next({
        name: to.name, // Redirect to the same route
        params: {
          ...to.params,
          workspaceUUID: activeWorkspaceUUID, // Inject active workspace UUID
        },
        query: to.query, // Preserve query parameters
        hash: to.hash, // Preserve hash
      });
      return; // Exit guard after redirecting
    }
  }

  // Proceed to the route if no redirection is needed
  next();
});

// Add a global afterEach navigation guard
router.afterEach((to, from, failure) => {
  globalThis.Intercom("update");
  // IN_APP engagement: on SUCCESSFUL view transitions, ask the engine "anything for me?"
  // (client-asks model). Fully dormant unless VITE_ENGAGEMENT_API_URL is set; failed navigations
  // are ignored; token acquisition inside the service is non-redirecting and unauthenticated
  // states just mean "no message" — this hook can never break or hijack navigation.
  if (failure || !isInAppMessagingEnabled()) return;
  import("@/stores/inapp-modal-store")
    .then(({ useInAppModalStore }) =>
      useInAppModalStore()
        .askForView(to.name)
        .catch(() => {}),
    )
    .catch(() => {});
});

export default router;
